import React, { useState, useEffect } from 'react'
import {
  Box,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Alert,
  CircularProgress,
  TextField,
  Link,
} from '@mui/material'
import { Link as RouterLink } from 'react-router-dom'
import api from '../../../services/api'

/**
 * Activity track type configuration component
 * Rendered within the Agent Config section for activity tracks.
 * Binds the pathway to an activity (activities.activity_key); the activity's
 * behavior prompt and content bank are viewable on the Activities page.
 */
function ActivityConfig({ config = {}, onChange }) {
  const [activities, setActivities] = useState([])
  const [loading, setLoading] = useState(false)
  const [loadError, setLoadError] = useState(null)

  useEffect(() => {
    const fetchActivities = async () => {
      setLoading(true)
      setLoadError(null)
      try {
        const response = await api.get('/activities')
        setActivities(response.data || [])
      } catch (error) {
        console.error('Failed to fetch activities:', error)
        setLoadError('Failed to load activities')
        setActivities([])
      } finally {
        setLoading(false)
      }
    }
    fetchActivities()
  }, [])

  const updateConfig = (field, value) => {
    onChange({ ...config, [field]: value })
  }

  const selected = activities.find((a) => a.activity_key === config.activity_key)

  return (
    <Box>
      <Typography variant="subtitle2" gutterBottom>
        Activity Configuration
      </Typography>

      {loadError && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {loadError}
        </Alert>
      )}

      {!config.activity_key && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Activity pathways require a bound activity — the conversation agent has no behavior
          script or content bank without one.
        </Alert>
      )}

      <FormControl fullWidth size="small" sx={{ mb: 2 }}>
        <InputLabel>Activity</InputLabel>
        <Select
          value={config.activity_key || ''}
          label="Activity"
          onChange={(e) => updateConfig('activity_key', e.target.value)}
          disabled={loading}
          startAdornment={loading ? <CircularProgress size={16} sx={{ mr: 1 }} /> : null}
        >
          {activities.map((a) => (
            <MenuItem key={a.activity_key} value={a.activity_key}>
              {a.name} ({a.activity_key})
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {selected && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            {selected.description}
          </Typography>
          <Box display="flex" gap={1} mt={1} alignItems="center">
            {(selected.entry_modes || []).map((m) => (
              <Chip key={m} label={m} size="small" variant="outlined" />
            ))}
            <Link component={RouterLink} to="/activities" variant="body2" underline="hover">
              View content bank & patient coverage
            </Link>
          </Box>
        </Box>
      )}

      <TextField
        label="Items per session"
        type="number"
        size="small"
        value={config.items_per_session ?? 3}
        onChange={(e) => updateConfig('items_per_session', parseInt(e.target.value, 10) || 3)}
        inputProps={{ min: 1, max: 10 }}
        sx={{ width: 180, mb: 2 }}
        helperText="Target items to cover per session"
      />

      <TextField
        label="Tag filter (optional)"
        size="small"
        fullWidth
        value={(config.item_tags || []).join(', ')}
        onChange={(e) =>
          updateConfig(
            'item_tags',
            e.target.value
              .split(',')
              .map((t) => t.trim())
              .filter(Boolean),
          )
        }
        helperText="Comma-separated tags; restricts which bank items this pathway delivers (empty = all)"
      />
    </Box>
  )
}

export default ActivityConfig
